import { useCallback, useEffect, useRef, useState } from "react";

import * as service from "../services/feedPopsService";

const DEFAULT_LIMIT = 10;
const MAX_POPS = 100;

const createSeed = () => {
  return `${Date.now()}-${Math.random().toString(36).slice(2)}`;
};

const normalizeLimit = (value) => {
  const parsed = Number(value);

  return Number.isSafeInteger(parsed) && parsed > 0
    ? Math.min(parsed, MAX_POPS)
    : DEFAULT_LIMIT;
};

const normalizePopId = (value) => {
  if (value === null || value === undefined || value === "") {
    return null;
  }

  const parsed = Number(value);

  return Number.isSafeInteger(parsed) && parsed > 0 ? parsed : null;
};

const normalizePops = (items = []) => {
  const seen = new Set();

  return (Array.isArray(items) ? items : [])
    .filter((pop) => {
      const id = normalizePopId(pop?.popId);

      if (!id || seen.has(id)) {
        return false;
      }

      seen.add(id);

      return true;
    })
    .slice(0, MAX_POPS);
};

const normalizeResponse = (response) => {
  const data = response?.data ?? response;

  if (data?.success === false) {
    throw new Error(data.message || "Unable to load Pops.");
  }

  if (!data || !Array.isArray(data.pops)) {
    throw new Error("Invalid Pops feed response.");
  }

  return data;
};

export const useFeedPops = ({
  limit = DEFAULT_LIMIT,
  initialPopId = null,
  seed,
  autoLoad = true,
} = {}) => {
  const parsedLimit = normalizeLimit(limit);

  const initialPopIdRef = useRef(normalizePopId(initialPopId));
  const seedRef = useRef(seed || createSeed());

  const [pops, setPops] = useState([]);
  const [loading, setLoading] = useState(Boolean(autoLoad));
  const [loadingMore, setLoadingMore] = useState(false);
  const [error, setError] = useState("");

  const [offset, setOffset] = useState(0);
  const [hasMore, setHasMore] = useState(true);
  const [activePopId, setActivePopId] = useState(null);

  const mountedRef = useRef(false);
  const fetchingRef = useRef(false);
  const requestIdRef = useRef(0);
  const abortControllerRef = useRef(null);

  const popsRef = useRef([]);
  const offsetRef = useRef(0);
  const hasMoreRef = useRef(true);

  const requestFeed = useCallback(
    async ({ nextOffset = 0, replace = false } = {}) => {
      const requestedOffset = Number(nextOffset);

      if (!Number.isSafeInteger(requestedOffset) || requestedOffset < 0) {
        return false;
      }

      if (fetchingRef.current) {
        return false;
      }

      if (
        !replace &&
        (!hasMoreRef.current || popsRef.current.length >= MAX_POPS)
      ) {
        return false;
      }

      fetchingRef.current = true;

      const requestId = ++requestIdRef.current;
      const controller = new AbortController();

      abortControllerRef.current = controller;

      if (requestedOffset === 0 || replace) {
        setLoading(true);
      } else {
        setLoadingMore(true);
      }

      setError("");

      try {
        const response = await service.getPopsFeed({
          limit: parsedLimit,
          offset: requestedOffset,
          initialPopId: initialPopIdRef.current,
          seed: seedRef.current,
          signal: controller.signal,
        });

        if (
          !mountedRef.current ||
          controller.signal.aborted ||
          requestId !== requestIdRef.current
        ) {
          return false;
        }

        const data = normalizeResponse(response);

        const incomingPops = normalizePops(data.pops);
        const pagination = data.pagination || {};

        const shouldReplace = replace || requestedOffset === 0;

        let nextPops;

        if (shouldReplace) {
          nextPops = incomingPops;
        } else {
          nextPops = normalizePops([...popsRef.current, ...incomingPops]);
        }

        popsRef.current = nextPops;
        setPops(nextPops);

        const reportedNextOffset = Number(pagination.nextOffset);

        const next =
          Number.isSafeInteger(reportedNextOffset) &&
          reportedNextOffset >= requestedOffset
            ? reportedNextOffset
            : requestedOffset + data.pops.length;

        const reportedHasMore =
          typeof pagination.hasMore === "boolean"
            ? pagination.hasMore
            : data.pops.length >= parsedLimit;

        const nextHasMore =
          reportedHasMore &&
          next > requestedOffset &&
          next < MAX_POPS &&
          nextPops.length < MAX_POPS;

        offsetRef.current = next;
        hasMoreRef.current = nextHasMore;

        setOffset(next);
        setHasMore(nextHasMore);

        if (shouldReplace) {
          const selectedId = initialPopIdRef.current;

          const selectedPop = selectedId
            ? nextPops.find((pop) => normalizePopId(pop.popId) === selectedId)
            : null;

          const firstPop = selectedPop || nextPops[0];

          setActivePopId(firstPop?.popId ?? null);
        }

        return data;
      } catch (requestError) {
        if (
          !mountedRef.current ||
          controller.signal.aborted ||
          requestId !== requestIdRef.current ||
          requestError?.code === "ERR_CANCELED" ||
          requestError?.name === "CanceledError" ||
          requestError?.name === "AbortError"
        ) {
          return false;
        }

        console.error("Unable to load Pops feed:", requestError);

        setError(
          requestError?.response?.data?.message ||
            requestError?.message ||
            "Unable to load Pops.",
        );

        return false;
      } finally {
        if (abortControllerRef.current === controller) {
          abortControllerRef.current = null;
        }

        if (requestId === requestIdRef.current) {
          fetchingRef.current = false;

          if (mountedRef.current) {
            setLoading(false);
            setLoadingMore(false);
          }
        }
      }
    },
    [parsedLimit],
  );

  const getPopsFeed = useCallback(
    (options = {}) => {
      return requestFeed(options);
    },
    [requestFeed],
  );

  const loadMore = useCallback(() => {
    if (
      fetchingRef.current ||
      !hasMoreRef.current ||
      popsRef.current.length >= MAX_POPS
    ) {
      return Promise.resolve(false);
    }

    return requestFeed({
      nextOffset: offsetRef.current,
      replace: false,
    });
  }, [requestFeed]);

  const refreshFeed = useCallback(async () => {
    abortControllerRef.current?.abort();

    requestIdRef.current += 1;
    fetchingRef.current = false;
    abortControllerRef.current = null;

    seedRef.current = createSeed();

    popsRef.current = [];
    offsetRef.current = 0;
    hasMoreRef.current = true;

    setPops([]);
    setOffset(0);
    setHasMore(true);
    setActivePopId(null);
    setError("");

    return requestFeed({
      nextOffset: 0,
      replace: true,
    });
  }, [requestFeed]);

  const setActivePop = useCallback((popId) => {
    setActivePopId(normalizePopId(popId));
  }, []);

  useEffect(() => {
    mountedRef.current = true;

    if (autoLoad) {
      void requestFeed({
        nextOffset: 0,
        replace: true,
      });
    }

    return () => {
      mountedRef.current = false;

      requestIdRef.current += 1;
      abortControllerRef.current?.abort();

      abortControllerRef.current = null;
      fetchingRef.current = false;
    };
  }, [autoLoad, requestFeed]);

  return {
    pops,
    loading,
    loadingMore,
    error,
    offset,
    hasMore,
    activePopId,
    getPopsFeed,
    loadMore,
    refreshFeed,
    setActivePop,
  };
};

export default useFeedPops;
